import { useEffect, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

interface CategoryData {
  label: string;
  count: number;
  percent: number;
  dot: string;
}

interface AnalyticsInsight {
  title: string;
  insight: string;
  action: string;
  icon: string;
  bg: string;
  text: string;
}

export default function SWDashboardPage() {
  const { user } = useAuth();

  const [cases, setCases] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [lastSync, setLastSync] = useState(
    new Date().toLocaleTimeString()
  );

  const today = new Date();

  const [selectedMonth, setSelectedMonth] = useState(
    today.getMonth() + 1
  );

  const [selectedYear, setSelectedYear] = useState(
    today.getFullYear()
  );

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setLastSync(new Date().toLocaleTimeString());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  
  useEffect(() => {
    if (!user?.uid) {
      setCases([]);
      setAlerts([]);
      return;
    }

    const loadDashboardData = async () => {
      // Fetch cases
      try {
        const casesUrl =
          `https://guardiancloudcare.com/backend/api/get_cases.php?uid=${encodeURIComponent(user.uid)}`;

        const casesResponse = await fetch(casesUrl);
        const casesText = await casesResponse.text();

        console.log("get_cases.php status:", casesResponse.status);
        console.log("get_cases.php response:", casesText);

        if (!casesResponse.ok) {
          throw new Error(`Cases API HTTP ${casesResponse.status}`);
        }

        const casesResult = JSON.parse(casesText);

        if (Array.isArray(casesResult)) {
          setCases(casesResult);
        } else if (Array.isArray(casesResult?.data)) {
          setCases(casesResult.data);
        } else {
          console.error("Unexpected cases response:", casesResult);
          setCases([]);
        }
      } catch (error) {
        console.error("Failed to fetch cases:", error);
        setCases([]);
      }

      // Fetch alerts — keep this request separate
      try {
        const alertsUrl =
          `https://guardiancloudcare.com/backend/api/get_alerts.php?uid=${encodeURIComponent(user.uid)}`;

        const alertsResponse = await fetch(alertsUrl);
        const alertsText = await alertsResponse.text();

        console.log("get_alerts.php status:", alertsResponse.status);
        console.log("get_alerts.php response:", alertsText);

        if (!alertsResponse.ok) {
          throw new Error(`Alerts API HTTP ${alertsResponse.status}`);
        }

        const alertsResult = JSON.parse(alertsText);

        if (Array.isArray(alertsResult)) {
          setAlerts(alertsResult);
        } else if (Array.isArray(alertsResult?.data)) {
          setAlerts(alertsResult.data);
        } else {
          console.error("Unexpected alerts response:", alertsResult);
          setAlerts([]);
        }
      } catch (error) {
        console.error("Failed to fetch alerts:", error);
        setAlerts([]);
      }
    };

    void loadDashboardData();
  }, [user?.uid]);


  const selectedMonthCases = cases.filter((c) => {
    if (!c.created_at) {
      return false;
    }

    const date = new Date(c.created_at);

    return (
      date.getMonth() + 1 === selectedMonth &&
      date.getFullYear() === selectedYear
    );
  });

  const totalCases = cases.length;

  const activeCases = cases.filter(
    (c) =>
      c.case_status === 'Open' ||
      c.case_status === 'Ongoing' ||
      c.case_status === 'For Approval' ||
      c.case_status === 'Active'
  ).length;

  const closedCases = cases.filter(
    (c) =>
      c.case_status === 'Closed' ||
      c.case_status === 'Resolved'
  ).length;

  const overdueTasks = alerts.filter(
    (a) =>
      a.type === 'critical' ||
      String(a.title || '')
        .toLowerCase()
        .includes('critical') ||
      String(a.desc || '')
        .toLowerCase()
        .includes('urgent')
  ).length;

  const selectedTotal = selectedMonthCases.length;

  const percent = (value: number) => {
    if (selectedTotal === 0) {
      return 0;
    }

    return Math.round(
      (value / selectedTotal) * 100
    );
  };

  const countIncident = (type: string) =>
    selectedMonthCases.filter(
      (c) =>
        String(c.incident_type || '')
          .trim()
          .toLowerCase() === type.toLowerCase()
    ).length;

  const neglectCases =
    countIncident('Neglect');

  const physicalCases =
    countIncident('Physical Abuse');

  const emotionalCases =
    countIncident('Emotional Abuse');

  const sexualCases =
    countIncident('Sexual Abuse');

  const otherCases = Math.max(
    selectedTotal -
      neglectCases -
      physicalCases -
      emotionalCases -
      sexualCases,
    0
  );

  const categoryData: CategoryData[] = [
    {
      label: 'Neglect',
      count: neglectCases,
      percent: percent(neglectCases),
      dot: 'bg-indigo-500',
    },
    {
      label: 'Physical Abuse',
      count: physicalCases,
      percent: percent(physicalCases),
      dot: 'bg-green-500',
    },
    {
      label: 'Emotional Abuse',
      count: emotionalCases,
      percent: percent(emotionalCases),
      dot: 'bg-orange-400',
    },
    {
      label: 'Sexual Abuse',
      count: sexualCases,
      percent: percent(sexualCases),
      dot: 'bg-purple-500',
    },
    {
      label: 'Other Concerns',
      count: otherCases,
      percent: percent(otherCases),
      dot: 'bg-slate-400',
    },
  ];

  const sortedCategoryData = [...categoryData].sort(
    (a, b) => b.count - a.count
  );

  const topConcern =
    selectedTotal > 0
      ? sortedCategoryData[0]
      : {
          label: 'No Data',
          count: 0,
          percent: 0,
          dot: 'bg-slate-400',
        };

  const chartSegments = [
    categoryData[0].percent,
    categoryData[1].percent,
    categoryData[2].percent,
    categoryData[3].percent,
  ];

  const chartStartOne = chartSegments[0];

  const chartStartTwo =
    chartStartOne + chartSegments[1];

  const chartStartThree =
    chartStartTwo + chartSegments[2];

  const chartStartFour =
    chartStartThree + chartSegments[3];

  const chartGradient =
    selectedTotal > 0
      ? `conic-gradient(
          #4f63ff 0 ${chartStartOne}%,
          #4caf73 ${chartStartOne}% ${chartStartTwo}%,
          #ffb03b ${chartStartTwo}% ${chartStartThree}%,
          #8b5cf6 ${chartStartThree}% ${chartStartFour}%,
          #cbd5e1 ${chartStartFour}% 100%
        )`
      : '#e5e7eb';

  const getMonthlyCases = (
    month: number,
    year: number
  ) => {
    return cases.filter((c) => {
      if (!c.created_at) {
        return false;
      }

      const date = new Date(c.created_at);

      return (
        date.getMonth() + 1 === month &&
        date.getFullYear() === year
      );
    }).length;
  };

  let previousMonth = selectedMonth - 1;
  let previousYear = selectedYear;

  if (previousMonth === 0) {
    previousMonth = 12;
    previousYear -= 1;
  }

  const previousMonthCases =
    getMonthlyCases(
      previousMonth,
      previousYear
    );

  const getChangePercent = (
    current: number,
    previous: number
  ) => {
    if (previous === 0 && current > 0) {
      return 100;
    }

    if (previous === 0 && current === 0) {
      return 0;
    }

    return Math.round(
      ((current - previous) / previous) * 100
    );
  };

  const monthlyChange =
    getChangePercent(
      selectedTotal,
      previousMonthCases
    );

  const monthLabel = new Date(
    selectedYear,
    selectedMonth - 1
  ).toLocaleString('en-US', {
    month: 'long',
  });

  const getTrendText = () => {
    if (selectedTotal === 0) {
      return 'No cases were recorded for the selected period.';
    }

    if (monthlyChange > 0) {
      return `Cases increased by ${monthlyChange}% compared with the previous month.`;
    }

    if (monthlyChange < 0) {
      return `Cases decreased by ${Math.abs(
        monthlyChange
      )}% compared with the previous month.`;
    }

    return 'The number of cases remained unchanged compared with the previous month.';
  };

  const getCategoryInsight =
    (): AnalyticsInsight => {
      if (selectedTotal === 0) {
        return {
          title: 'No Category Insight',
          insight:
            'There are no recorded cases for the selected month and year, so a category-based insight cannot be generated.',
          action:
            'Continue monitoring incoming cases and review the analytics again when case records become available.',
          icon: 'ri-information-line',
          bg: 'bg-gray-100',
          text: 'text-gray-600',
        };
      }

      const category =
        topConcern.label.toLowerCase();

      if (category.includes('neglect')) {
        return {
          title: 'Neglect Cases Require Attention',
          insight: `${topConcern.label} is the most frequently recorded category with ${topConcern.count} case(s), representing ${topConcern.percent}% of the selected period.`,
          action:
            'Consider prioritizing parenting education, caregiver support, and child protection awareness activities for the affected community.',
          icon: 'ri-parent-line',
          bg: 'bg-indigo-100',
          text: 'text-indigo-600',
        };
      }

      if (
        category.includes('physical')
      ) {
        return {
          title:
            'Physical Abuse Cases Require Attention',
          insight: `${topConcern.label} is the most frequently recorded category with ${topConcern.count} case(s), representing ${topConcern.percent}% of the selected period.`,
          action:
            'Consider strengthening positive parenting, non-violent discipline, and child protection awareness activities.',
          icon: 'ri-shield-cross-line',
          bg: 'bg-green-100',
          text: 'text-green-600',
        };
      }

      if (
        category.includes('sexual')
      ) {
        return {
          title:
            'Sexual Abuse Cases Require Attention',
          insight: `${topConcern.label} is the most frequently recorded category with ${topConcern.count} case(s), representing ${topConcern.percent}% of the selected period.`,
          action:
            'Consider strengthening child protection, abuse prevention, reporting awareness, and appropriate referral activities.',
          icon: 'ri-shield-user-line',
          bg: 'bg-purple-100',
          text: 'text-purple-600',
        };
      }

      if (
        category.includes('emotional')
      ) {
        return {
          title:
            'Emotional Abuse Cases Require Attention',
          insight: `${topConcern.label} is the most frequently recorded category with ${topConcern.count} case(s), representing ${topConcern.percent}% of the selected period.`,
          action:
            'Consider caregiver education and child well-being activities focused on emotional support and healthy family relationships.',
          icon: 'ri-heart-pulse-line',
          bg: 'bg-orange-100',
          text: 'text-orange-600',
        };
      }

      return {
        title: 'Other Concerns Require Review',
        insight: `${topConcern.label} represents the largest recorded group with ${topConcern.count} case(s), representing ${topConcern.percent}% of the selected period.`,
        action:
          'Review the recorded case descriptions and identify whether additional community education, referral, or intervention activities are appropriate.',
        icon: 'ri-search-eye-line',
        bg: 'bg-slate-100',
        text: 'text-slate-600',
      };
    };

  const categoryInsight =
    getCategoryInsight();

  const getTrendInsight =
    (): AnalyticsInsight => {
      if (selectedTotal === 0) {
        return {
          title: 'No Trend Insight',
          insight:
            'There are no recorded cases for the selected period.',
          action:
            'Continue monitoring the case records for future changes in the number of reported cases.',
          icon: 'ri-line-chart-line',
          bg: 'bg-gray-100',
          text: 'text-gray-600',
        };
      }

      if (monthlyChange > 0) {
        return {
          title: 'Increasing Case Activity',
          insight:
            getTrendText(),
          action:
            'Review the affected case categories and determine whether additional follow-up, intervention, or community-level prevention activities are needed.',
          icon: 'ri-arrow-up-line',
          bg: 'bg-red-100',
          text: 'text-red-600',
        };
      }

      if (monthlyChange < 0) {
        return {
          title: 'Decreasing Case Activity',
          insight:
            getTrendText(),
          action:
            'Continue monitoring case patterns and maintain appropriate follow-up and prevention activities.',
          icon: 'ri-arrow-down-line',
          bg: 'bg-green-100',
          text: 'text-green-600',
        };
      }

      return {
        title: 'Stable Case Activity',
        insight:
          getTrendText(),
        action:
          'Continue monitoring the selected period and review category-level patterns for emerging concerns.',
        icon: 'ri-subtract-line',
        bg: 'bg-amber-100',
        text: 'text-amber-600',
      };
    };

  const trendInsight =
    getTrendInsight();

  const caseCards = [
    {
      title: 'Total Cases',
      value: totalCases,
      icon: 'ri-briefcase-line',
      bg: 'bg-indigo-100',
      text: 'text-indigo-600',
    },
    {
      title: 'Active Cases',
      value: activeCases,
      icon: 'ri-folder-line',
      bg: 'bg-green-100',
      text: 'text-green-600',
    },
    {
      title: 'Closed Cases',
      value: closedCases,
      icon: 'ri-checkbox-circle-line',
      bg: 'bg-orange-100',
      text: 'text-orange-600',
    },
    {
      title: 'Overdue Tasks',
      value: overdueTasks,
      icon: 'ri-error-warning-line',
      bg: 'bg-red-100',
      text: 'text-red-600',
    },
  ];

  const monthlyData = Array.from({
    length: 6,
  }).map((_, index) => {
    const date = new Date(
      selectedYear,
      selectedMonth - 1,
      1
    );

    date.setMonth(
      date.getMonth() - (5 - index)
    );

    const month =
      date.getMonth() + 1;

    const year =
      date.getFullYear();

    return {
      label: date.toLocaleDateString(
        'en-US',
        {
          month: 'short',
          year: '2-digit',
        }
      ),
      value: getMonthlyCases(
        month,
        year
      ),
    };
  });

  const maxChartValue = Math.max(
    ...monthlyData.map(
      (m) => m.value
    ),
    1
  );

  const chartPoints = monthlyData
    .map((m, index) => {
      const x = 55 + index * 75;

      const y =
        180 -
        (m.value /
          maxChartValue) *
          120;

      return `${x},${y}`;
    })
    .join(' ');

  return (
    <AppLayout
      title="My Dashboard"
      breadcrumb={[
        'Social Worker',
        'My Dashboard',
      ]}
    >
      <div className="flex items-center gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-200 px-3 py-2 rounded-xl mb-5 w-fit">
        <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
        Live · Last sync: {lastSync}
      </div>

      <div className="min-h-screen bg-gray-50 p-6 rounded-2xl">
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {caseCards.map((item) => (
                <div
                  key={item.title}
                  className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-full ${item.bg} flex items-center justify-center`}
                    >
                      <i
                        className={`${item.icon} ${item.text} text-2xl`}
                      />
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 font-medium">
                        {item.title}
                      </p>

                      <h2 className="text-3xl font-bold text-gray-900">
                        {item.value}
                      </h2>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-4 flex items-center gap-1">
                    <span
                      className={
                        monthlyChange >= 0
                          ? 'text-green-600'
                          : 'text-red-600'
                      }
                    >
                      {monthlyChange >= 0
                        ? '↑'
                        : '↓'}{' '}
                      {Math.abs(
                        monthlyChange
                      )}
                      %
                    </span>

                    vs previous month
                  </p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-semibold text-gray-800">
                    Cases Trend
                  </h3>

                  <div className="flex gap-2">
                    <select
                      value={
                        selectedMonth
                      }
                      onChange={(e) =>
                        setSelectedMonth(
                          Number(
                            e.target.value
                          )
                        )
                      }
                      className="text-xs border border-gray-200 px-3 py-2 rounded-lg text-gray-600"
                    >
                      {months.map(
                        (
                          month,
                          index
                        ) => (
                          <option
                            key={
                              month
                            }
                            value={
                              index +
                              1
                            }
                          >
                            {month}
                          </option>
                        )
                      )}
                    </select>

                    <select
                      value={
                        selectedYear
                      }
                      onChange={(e) =>
                        setSelectedYear(
                          Number(
                            e.target.value
                          )
                        )
                      }
                      className="text-xs border border-gray-200 px-3 py-2 rounded-lg text-gray-600"
                    >
                      {[
                        2024,
                        2025,
                        2026,
                        2027,
                      ].map(
                        (year) => (
                          <option
                            key={
                              year
                            }
                            value={
                              year
                            }
                          >
                            {year}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>

                <div className="h-56 relative">
                  <svg
                    viewBox="0 0 500 240"
                    className="w-full h-full"
                  >
                    <line
                      x1="40"
                      y1="25"
                      x2="480"
                      y2="25"
                      stroke="#e5e7eb"
                    />

                    <line
                      x1="40"
                      y1="75"
                      x2="480"
                      y2="75"
                      stroke="#e5e7eb"
                    />

                    <line
                      x1="40"
                      y1="125"
                      x2="480"
                      y2="125"
                      stroke="#e5e7eb"
                    />

                    <line
                      x1="40"
                      y1="175"
                      x2="480"
                      y2="175"
                      stroke="#e5e7eb"
                    />

                    <polyline
                      points={
                        chartPoints
                      }
                      fill="none"
                      stroke="#6366f1"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    <polygon
                      points={`${chartPoints} 430,200 55,200`}
                      fill="#6366f1"
                      opacity="0.08"
                    />

                    {monthlyData.map(
                      (
                        m,
                        index
                      ) => {
                        const x =
                          55 +
                          index *
                            75;

                        const y =
                          180 -
                          (m.value /
                            maxChartValue) *
                            120;

                        return (
                          <g
                            key={
                              m.label
                            }
                          >
                            <circle
                              cx={x}
                              cy={y}
                              r="6"
                              fill="#6366f1"
                            />

                            <text
                              x={
                                x -
                                8
                              }
                              y={
                                y -
                                14
                              }
                              fontSize="13"
                              fill="#6b7280"
                              fontWeight="600"
                            >
                              {
                                m.value
                              }
                            </text>

                            <text
                              x={
                                x -
                                18
                              }
                              y="225"
                              fontSize="12"
                              fill="#6b7280"
                            >
                              {
                                m.label
                              }
                            </text>
                          </g>
                        );
                      }
                    )}
                  </svg>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-semibold text-gray-800">
                    Cases by Category
                  </h3>

                  <div className="flex gap-2">
                    <select
                      value={
                        selectedMonth
                      }
                      onChange={(e) =>
                        setSelectedMonth(
                          Number(
                            e.target.value
                          )
                        )
                      }
                      className="text-xs border border-gray-200 px-3 py-2 rounded-lg text-gray-600"
                    >
                      {months.map(
                        (
                          month,
                          index
                        ) => (
                          <option
                            key={
                              month
                            }
                            value={
                              index +
                              1
                            }
                          >
                            {month}
                          </option>
                        )
                      )}
                    </select>

                    <select
                      value={
                        selectedYear
                      }
                      onChange={(e) =>
                        setSelectedYear(
                          Number(
                            e.target.value
                          )
                        )
                      }
                      className="text-xs border border-gray-200 px-3 py-2 rounded-lg text-gray-600"
                    >
                      {[
                        2024,
                        2025,
                        2026,
                        2027,
                      ].map(
                        (year) => (
                          <option
                            key={
                              year
                            }
                            value={
                              year
                            }
                          >
                            {year}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-center">
                  <div
                    className="relative w-48 h-48 mx-auto rounded-full"
                    style={{
                      background:
                        chartGradient,
                    }}
                  >
                    <div className="absolute inset-10 rounded-full bg-white flex flex-col items-center justify-center">
                      <h2 className="text-3xl font-bold text-gray-900">
                        {
                          selectedTotal
                        }
                      </h2>

                      <p className="text-xs text-gray-400">
                        {
                          months[
                            selectedMonth -
                              1
                          ]
                        }{' '}
                        {
                          selectedYear
                        }
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4 text-sm">
                    {categoryData.map(
                      (item) => (
                        <div
                          key={
                            item.label
                          }
                          className="flex items-center justify-between gap-4"
                        >
                          <span className="flex items-center gap-2 text-gray-600">
                            <span
                              className={`w-3 h-3 rounded-full ${item.dot}`}
                            />

                            {
                              item.label
                            }
                          </span>

                          <span className="font-semibold text-gray-700">
                            {
                              item.count
                            }{' '}
                            (
                            {
                              item.percent
                            }
                            %)
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <i className="ri-bar-chart-grouped-line text-blue-600 text-lg" />
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-800">
                      System Insight & Recommendation
                    </h3>

                    <p className="text-xs text-gray-400 mt-0.5">
                      Analytics interpretation for{' '}
                      {monthLabel}{' '}
                      {selectedYear}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-5">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl ${categoryInsight.bg} flex items-center justify-center flex-shrink-0`}
                    >
                      <i
                        className={`${categoryInsight.icon} ${categoryInsight.text} text-lg`}
                      />
                    </div>

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                        System Insight
                      </p>

                      <h4 className="text-sm font-bold text-gray-800 mt-1">
                        {
                          categoryInsight.title
                        }
                      </h4>

                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                        {
                          categoryInsight.insight
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-teal-100 bg-teal-50 p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                      <i className="ri-lightbulb-line text-teal-600 text-lg" />
                    </div>

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-teal-600">
                        Possible Action
                      </p>

                      <h4 className="text-sm font-bold text-gray-800 mt-1">
                        Recommended Follow-up
                      </h4>

                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                        {
                          categoryInsight.action
                        }
                      </p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2 rounded-xl border border-gray-100 bg-white p-5">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl ${trendInsight.bg} flex items-center justify-center flex-shrink-0`}
                    >
                      <i
                        className={`${trendInsight.icon} ${trendInsight.text} text-lg`}
                      />
                    </div>

                    <div className="flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                        Trend Insight
                      </p>

                      <h4 className="text-sm font-bold text-gray-800 mt-1">
                        {
                          trendInsight.title
                        }
                      </h4>

                      <p className="text-sm text-gray-600 mt-2">
                        {
                          trendInsight.insight
                        }
                      </p>

                      <div className="mt-3 bg-gray-50 rounded-lg px-4 py-3">
                        <p className="text-xs font-semibold text-gray-500">
                          Suggested Action
                        </p>

                        <p className="text-sm text-gray-600 mt-1">
                          {
                            trendInsight.action
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm h-fit">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 mb-5">
              <i className="ri-line-chart-line text-indigo-600" />
              Analytics Insights
            </h3>

            <div className="space-y-4">
              <div className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <i className="ri-line-chart-line text-green-600 text-lg" />
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">
                      Cases Trend
                    </h4>

                    <p className="text-xs text-gray-500 mt-1">
                      {getTrendText()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <i className="ri-alarm-warning-line text-orange-600 text-lg" />
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">
                      Top Concern
                    </h4>

                    <p className="text-xs text-gray-500 mt-1">
                      {topConcern.label ===
                      'No Data'
                        ? 'No case category is available for the selected period.'
                        : `${topConcern.label} has ${topConcern.count} case(s), equal to ${topConcern.percent}% for the selected month.`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <i className="ri-time-line text-purple-600 text-lg" />
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">
                      Active Cases
                    </h4>

                    <p className="text-xs text-gray-500 mt-1">
                      There are{' '}
                      {activeCases}{' '}
                      active cases from your authorized case records.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border border-teal-100 rounded-xl p-4 bg-teal-50">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                    <i className="ri-lightbulb-line text-teal-600 text-lg" />
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">
                      Recommended Action
                    </h4>

                    <p className="text-xs text-gray-600 mt-1">
                      {
                        categoryInsight.action
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </AppLayout>
  );
}